import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import type { PermissionKey } from '@evimo/types/enums/permission-key';
import {
  isEditableSystemRoleName,
  SystemRoleName,
} from '@evimo/types/enums/system-role-name';
import { useAuth } from '~/app/auth/context/AuthContext';
import type { RoleDetailDto } from '~/app/services/authorization/role.service';
import { RoleService } from '~/app/services/authorization/role.service';
import { isRolePermissionsEditable, permissionKeysEqual } from '../utils';
import { resolveAuthorizationErrorMessage } from '../utils/resolve-authorization-error';

type UseRolePermissionsEditorOptions = {
  role: RoleDetailDto | null;
  organizationId: string | null;
  useSystemRoleApi: boolean;
  canAssignPermissions: boolean;
  allPermissionKeys: PermissionKey[];
  onSaved: () => void;
};

export function useRolePermissionsEditor({
  role,
  organizationId,
  useSystemRoleApi,
  canAssignPermissions,
  allPermissionKeys,
  onSaved,
}: UseRolePermissionsEditorOptions) {
  const { t } = useTranslation('authorization');
  const { message, notification } = App.useApp();
  const { user } = useAuth();
  const systemRoleWarningShownRef = useRef(false);

  const [savedPermissionKeys, setSavedPermissionKeys] = useState<
    PermissionKey[]
  >([]);
  const [draftPermissionKeys, setDraftPermissionKeys] = useState<
    PermissionKey[]
  >([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isPermissionsUpdating, startPermissionsTransition] = useTransition();

  useEffect(() => {
    const keys = role?.permissionKeys ?? [];
    setSavedPermissionKeys(keys);
    setDraftPermissionKeys(keys);
    systemRoleWarningShownRef.current = false;
  }, [role?.id, role?.permissionKeys]);

  const isRoleEditable = useMemo(() => {
    if (!role || !canAssignPermissions || !user) return false;
    return isRolePermissionsEditable(role, user);
  }, [canAssignPermissions, role, user]);

  const isDirty = useMemo(
    () => !permissionKeysEqual(savedPermissionKeys, draftPermissionKeys),
    [draftPermissionKeys, savedPermissionKeys],
  );

  const savedPermissionKeySet = useMemo(
    () => new Set(savedPermissionKeys),
    [savedPermissionKeys],
  );

  const draftPermissionKeySet = useMemo(
    () => new Set(draftPermissionKeys),
    [draftPermissionKeys],
  );

  const isPermissionDirty = useCallback(
    (permissionKey: PermissionKey) =>
      savedPermissionKeySet.has(permissionKey) !==
      draftPermissionKeySet.has(permissionKey),
    [draftPermissionKeySet, savedPermissionKeySet],
  );

  const maybeShowSystemRoleWarning = useCallback(() => {
    if (
      systemRoleWarningShownRef.current ||
      !role?.isSystemRole ||
      !isEditableSystemRoleName(role.name) ||
      user?.roleName !== SystemRoleName.SUPER_ADMIN
    ) {
      return;
    }

    systemRoleWarningShownRef.current = true;
    notification.warning({
      message: t('systemRoleEditWarning.title'),
      description: t('systemRoleEditWarning.description'),
      duration: 6,
    });
  }, [notification, role, t, user?.roleName]);

  const onTogglePermission = useCallback(
    (permissionKey: PermissionKey) => {
      if (!isRoleEditable) return;

      setDraftPermissionKeys((current) => {
        const hasPermission = current.includes(permissionKey);
        const nextKeys = hasPermission
          ? current.filter((key) => key !== permissionKey)
          : [...current, permissionKey];

        maybeShowSystemRoleWarning();
        return nextKeys;
      });
    },
    [isRoleEditable, maybeShowSystemRoleWarning],
  );

  const onSetAllPermissions = useCallback(
    (selectAll: boolean) => {
      if (!isRoleEditable || isPermissionsUpdating) return;

      startPermissionsTransition(() => {
        setDraftPermissionKeys(selectAll ? [...allPermissionKeys] : []);
        if (selectAll) {
          maybeShowSystemRoleWarning();
        }
      });
    },
    [
      allPermissionKeys,
      isPermissionsUpdating,
      isRoleEditable,
      maybeShowSystemRoleWarning,
      startPermissionsTransition,
    ],
  );

  const allPermissionsSelected = useMemo(
    () =>
      allPermissionKeys.length > 0 &&
      permissionKeysEqual(draftPermissionKeys, allPermissionKeys),
    [allPermissionKeys, draftPermissionKeys],
  );

  const onDiscardChanges = useCallback(() => {
    if (isPermissionsUpdating) return;

    startPermissionsTransition(() => {
      setDraftPermissionKeys(savedPermissionKeys);
    });
  }, [isPermissionsUpdating, savedPermissionKeys, startPermissionsTransition]);

  const saveChanges = useCallback(async (): Promise<boolean> => {
    if (!role || !isDirty) return false;

    setIsSaving(true);
    try {
      const payload = { permissionKeys: draftPermissionKeys };

      if (useSystemRoleApi) {
        await RoleService.setSystemRolePermissions(role.id, payload);
      } else {
        if (!organizationId) return false;
        await RoleService.setRolePermissions(organizationId, role.id, payload);
      }

      setSavedPermissionKeys([...draftPermissionKeys]);
      message.success(t('messages.permissionsSaveSuccess'));
      onSaved();
      return true;
    } catch (error) {
      message.error(
        resolveAuthorizationErrorMessage(
          error,
          t,
          'messages.permissionsSaveError',
        ),
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [
    draftPermissionKeys,
    isDirty,
    message,
    onSaved,
    organizationId,
    role,
    t,
    useSystemRoleApi,
  ]);

  const onConfirmChanges = useCallback(() => {
    setIsConfirmModalOpen(true);
  }, []);

  const onConfirmModalClose = useCallback(() => {
    if (!isSaving) {
      setIsConfirmModalOpen(false);
    }
  }, [isSaving]);

  const onConfirmSave = useCallback(async () => {
    const saved = await saveChanges();
    if (saved) {
      setIsConfirmModalOpen(false);
    }
  }, [saveChanges]);

  const isEditableSystemRole = Boolean(
    role?.isSystemRole && role.name && isEditableSystemRoleName(role.name),
  );

  return {
    draftPermissionKeys,
    isDirty,
    isSaving,
    isPermissionsUpdating,
    isRoleEditable,
    isPermissionDirty,
    onTogglePermission,
    onSetAllPermissions,
    allPermissionsSelected,
    onConfirmChanges,
    onDiscardChanges,
    isConfirmModalOpen,
    onConfirmModalClose,
    onConfirmSave,
    isEditableSystemRole,
  };
}
